import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import { Search, Plus, User, Phone, RefreshCw } from 'lucide-react';
import axios from 'axios'; // Import axios for API calls
import API_BASE_URL from '@/config';

export const PatientList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState([]); // State to store patients
  const [loading, setLoading] = useState(false);

  // Fetch patients from the backend
  const fetchPatients = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/patients`); // Use API_BASE_URL
      setPatients(response.data || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.phone.includes(searchTerm)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-start justify-between gap-3 w-full sm:w-auto">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">Patient Management</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">View and manage all patient records</p>
          </div>
          {/* Mobile Icon-only Refresh Button */}
          <Button
            variant="outline"
            size="icon"
            onClick={fetchPatients}
            disabled={loading}
            className="sm:hidden h-9 w-9 flex-shrink-0"
            aria-label="Refresh patients"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Desktop Refresh Button */}
          <Button
            variant="outline"
            onClick={fetchPatients}
            disabled={loading}
            className="hidden sm:inline-flex h-11 gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button asChild className="w-full sm:w-auto h-11">
            <Link to="/admin/patients/new">
              <Plus className="h-4 w-4 mr-2" />
              Add New Patient
            </Link>
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="shadow-card">
        <CardContent className="p-4 sm:p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by name or phone number..."
              className="pl-10 h-11 text-sm sm:text-base"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Patient List */}
      <Card className="shadow-card">
        <CardHeader className="border-b border-border py-4 px-4 sm:px-6">
          <CardTitle className="font-serif text-xl">All Patients ({filteredPatients.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Mobile Card List (< md) */}
          <div className="block md:hidden divide-y divide-border">
            {filteredPatients.length > 0 ? (
              filteredPatients.map((patient) => (
                <div key={patient.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 p-2.5 rounded-xl flex-shrink-0">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900 text-base">
                          {patient.name}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {patient.age} yrs • <span className="capitalize">{patient.gender}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs bg-muted/40 p-2.5 rounded-lg">
                    <div>
                      <span className="text-muted-foreground block text-[11px]">Phone</span>
                      {patient.phone ? (
                        <a href={`tel:${patient.phone}`} className="font-medium text-primary hover:underline flex items-center gap-1 mt-0.5">
                          <Phone className="h-3 w-3" />
                          {patient.phone}
                        </a>
                      ) : (
                        <span className="text-muted-foreground">Not provided</span>
                      )}
                    </div>
                    <div>
                      <span className="text-muted-foreground block text-[11px]">Last Visit</span>
                      <span className="font-medium text-slate-700 mt-0.5 block">
                        {(patient.lastVisit || patient.last_visit) ? new Date(patient.lastVisit || patient.last_visit).toLocaleDateString() : '—'}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <Button variant="outline" className="h-11 text-sm font-medium" asChild>
                      <Link to={`/admin/patients/${patient.id}`}>View Details</Link>
                    </Button>
                    <Button variant="secondary" className="h-11 text-sm font-medium" asChild>
                      <Link to={`/admin/patients/edit/${patient.id}`}>Edit Patient</Link>
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm p-4">
                No patients found. Try a different search term.
              </div>
            )}
          </div>

          {/* Desktop Table (md+) */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Gender</TableHead>
                  <TableHead className="text-center">Phone</TableHead>
                  <TableHead>Last Visit</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPatients.length > 0 ? (
                  filteredPatients.map((patient) => (
                    <TableRow key={patient.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="bg-primary/10 p-2 rounded-lg">
                            <User className="h-4 w-4 text-primary" />
                          </div>
                          {patient.name}
                        </div>
                      </TableCell>
                      <TableCell>{patient.age}</TableCell>
                      <TableCell>{patient.gender}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          {patient.phone}
                        </div>
                      </TableCell>
                      <TableCell>{(patient.lastVisit || patient.last_visit) ? new Date(patient.lastVisit || patient.last_visit).toLocaleDateString() : '—'}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-2">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/admin/patients/${patient.id}`}>View</Link>
                          </Button>
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/admin/patients/edit/${patient.id}`}>Edit</Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No patients found. Try a different search term.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientList;
